import { Vector3, type BatchedMesh, type Material, type Matrix4 } from 'three';
import { standingClip } from './citizens/citizenActing';
import type { CitizenAsset, CitizenFigure } from './citizens/citizenAsset';
import { createCitizenBatch } from './citizens/citizenBatch';
import { clipPoseAt, skinnedVertex, walkPose } from './citizens/citizenPose';
import { citizenGeometry } from './citizens/citizenShapes';
import type { CitizenRole, LifeRoute } from './types';

/**
 * The trades whose figures play each role of the cast, the men's and the women's
 * (docs/CITIZENS.md). The cast are the few with a routine of their own: a baker at his door, the
 * watch on its rounds. They are drawn as the crowd is, with the citizens made for the city.
 */
export const CAST_TRADES: Record<
  CitizenRole,
  { readonly men: readonly string[]; readonly women: readonly string[] }
> = {
  friar: { men: ['friar'], women: ['sister'] },
  sister: { men: ['sister'], women: ['sister'] },
  burgomaster: { men: ['burgomaster'], women: ['gentlewoman'] },
  gentleperson: { men: ['burgher', 'merchant'], women: ['gentlewoman'] },
  sailor: { men: ['sailor', 'fisherman'], women: ['fishwife'] },
  shipmaster: { men: ['shipmaster'], women: ['gentlewoman'] },
  trader: { men: ['merchant', 'farmer', 'fishmonger'], women: ['market_woman', 'farm_woman'] },
  baker: { men: ['baker'], women: ['bakers_wife'] },
  butcher: { men: ['butcher'], women: ['housewife'] },
  craftsman: {
    men: ['carpenter', 'cooper', 'smith', 'mason', 'shoemaker', 'weaver'],
    women: ['townswoman'],
  },
  watchman: { men: ['watchman'], women: ['townswoman'] },
  schout: { men: ['schout'], women: ['gentlewoman'] },
  burglar: { men: ['burglar'], women: ['townswoman'] },
  carer: { men: ['baker'], women: ['bakers_wife'] },
  physician: { men: ['burgher'], women: ['gentlewoman'] },
};

/** One of the cast at a moment. */
export interface CastAct {
  /** Metres walked, the figure's size against life size, and how far it is into its stride:
   *  0 standing, 1 walking. */
  readonly walked: number;
  readonly scale: number;
  readonly gait: number;
  /** At its trade's own work, where it stands. */
  readonly working: boolean;
  readonly seconds: number;
}

export interface CastCitizens {
  readonly mesh: BatchedMesh;
  readonly figures: readonly CitizenFigure[];
  begin(): void;
  /** Draws the next of the cast, in the order of its routes, where `base` puts it. */
  draw(base: Matrix4, act: CastAct): void;
  end(): void;
  /** Where member `i`'s free hand is in its own frame, as it was last drawn: what it carries
   *  hangs from there. */
  hand(i: number, out: Vector3): Vector3;
}

/* Knuth's multiplier: neighbours in number are far apart in figure. */
const SPREAD = 2654435761;
/* The cast begin their standing clips this long apart, so two at one door do not move as one. */
const APART_S = 1.7;

/** The cast's people as citizens, one figure each of its role's trades, in one batch. */
export function createCastCitizens(
  material: Material,
  asset: CitizenAsset,
  routes: readonly LifeRoute[]
): CastCitizens {
  const cast = routes.map((route, i) => {
    const trades = CAST_TRADES[route.role as CitizenRole];
    const wanted = route.appearance === 'woman' ? trades.women : trades.men;
    const pool = asset.figures.flatMap((figure, f) => (wanted.includes(figure.trade) ? [f] : []));
    if (pool.length === 0) throw new Error(`citizens: no figure of ${wanted.join(', ')}`);
    return pool[Math.floor(((Math.imul(i + 1, SPREAD) >>> 0) / 2 ** 32) * pool.length)]!;
  });
  const used = [...new Set(cast)];
  const batch = createCitizenBatch(
    material,
    used.map((figure) => citizenGeometry(asset, asset.figures[figure]!.lods[0]!)),
    Math.max(1, routes.length),
    'life-citizens-body'
  );
  /* The vertex of each figure's free hand that hangs lowest: where a ring is held. The left
     hand is free but for one who carries a basket on that arm. */
  const grips = used.map((figure) => {
    const { lods, clips } = asset.figures[figure]!;
    const hand = asset.bones.indexOf(clips.idle === 'IdleCarry' ? 'hand.R' : 'hand.L');
    const lod = lods[0]!;
    let grip = 0,
      lowest = Infinity;
    for (let v = 0; v < lod.vertices; v++)
      for (let c = 0; c < 4; c++) {
        if (lod.joints[v * 4 + c] !== hand || lod.weights[v * 4 + c]! < 128) continue;
        const y = lod.places[v * 3 + 1]!;
        if (y < lowest) [lowest, grip] = [y, v];
      }
    return grip;
  });
  const poses = new Float32Array(routes.length * 3);
  let next = 0;
  return {
    mesh: batch.mesh,
    figures: cast.map((index) => asset.figures[index]!),
    begin() {
      batch.begin();
      next = 0;
    },
    draw(base, act) {
      const figure = asset.figures[cast[next]!]!;
      const standing = standingClip(figure, act.working ? 'work' : 'idle', 1, 0, 1);
      const stand = clipPoseAt(asset.clips[standing]!, act.seconds + next * APART_S);
      const walk = walkPose(asset.clips[figure.clips.walk]!, act.walked, act.scale);
      poses.set([stand, walk, act.gait], next * 3);
      batch.draw(used.indexOf(cast[next]!), base, stand, walk, act.gait);
      next++;
    },
    end() {
      batch.end();
    },
    hand(i, out) {
      const shape = used.indexOf(cast[i]!);
      const [x, y, z] = skinnedVertex(
        asset,
        asset.figures[cast[i]!]!.lods[0]!,
        grips[shape]!,
        poses[i * 3]!,
        poses[i * 3 + 1],
        poses[i * 3 + 2]
      );
      return out.set(x, y, z);
    },
  };
}
